/**
 * Status Transition Utilities
 *
 * Single source of truth for "what can this record become next, and who is
 * allowed to move it there". Previously this lived inside the
 * StatusTransitionBar organism, which meant the transitions could only ever be
 * rendered as their own separate row of buttons. The detail pages now fold the
 * available transitions into their main action cluster (see
 * `molecules/DetailActionBar`) so mobile shows one coherent set of actions
 * instead of two competing bars — which requires the pages themselves to be
 * able to ask for the transitions.
 *
 * Work Order Status Transitions (canonical lifecycle: pending -> in_progress
 * -> in_review -> approved -> completed; see WORK_ORDER_STATUSES in
 * utils/constants.ts and WorkOrder::STATUSES in the backend):
 *	 pending -> in_progress | cancelled  (database default status)
 *	 in_progress -> completed | on_hold | cancelled
 *	 in_review -> approved | in_progress (request changes) | cancelled
 *	 approved -> completed | cancelled
 *	 on_hold -> in_progress | cancelled
 *	 completed -> invoiced   (Super Admin only)
 *	 invoiced -> closed      (Super Admin only)
 *
 * in_review is ENTERED via the checklist workflow overlay
 * (checklistService.submitForVerification), not a button here — the dedicated
 * endpoint stamps the submitted_for_review_* audit fields and emails the
 * approver pool, which a bare status PATCH would skip. This map only needs to
 * offer the way OUT of in_review (approve / send back).
 *
 * Inspection Status Transitions:
 *	 pending -> in_progress | cancelled
 *	 in_progress -> observations_complete | cancelled
 *	 observations_complete -> completed (remediation, if any, lives on
 *	                          follow-up work orders — see Create Follow-Up)
 *	 corrections_in_progress -> completed | cancelled (legacy rows only:
 *	                          nothing enters this status any more)
 */
import type { UserRole } from './constants';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** Supported entity types for status transitions */
export type TransitionEntityType = 'work_order' | 'inspection';

/** Definition of a single status transition action */
export interface StatusTransition {
	/** The new status value to transition to */
	toStatus: string;
	/** Button label text */
	label: string;
	/** Intent: 'primary' for forward progress, 'secondary' for pausing, 'danger' for cancellation */
	variant: 'primary' | 'secondary' | 'danger';
	/** Roles that are allowed to perform this transition */
	allowedRoles: UserRole[];
}

/* ============================================
 * TRANSITION MAPS
 * ============================================ */

/**
 * Work order status transitions keyed by current status.
 * Each entry defines the available actions from that status.
 */
const WORK_ORDER_TRANSITIONS: Record<string, StatusTransition[]> = {
	/* 'pending' is the default status when a work order is created in the database.
	 * Technicians, managers, and admins can start work directly from pending. */
	pending: [
		{ toStatus: 'in_progress', label: 'Start Work', variant: 'primary', allowedRoles: ['admin', 'manager', 'technician'] },
		{ toStatus: 'cancelled', label: 'Cancel', variant: 'danger', allowedRoles: ['admin', 'manager'] },
	],
	in_progress: [
		{ toStatus: 'completed', label: 'Complete', variant: 'primary', allowedRoles: ['admin', 'manager', 'technician'] },
		{ toStatus: 'on_hold', label: 'Put On Hold', variant: 'secondary', allowedRoles: ['admin', 'manager'] },
		{ toStatus: 'cancelled', label: 'Cancel', variant: 'danger', allowedRoles: ['admin', 'manager'] },
	],
	/* Review states — supervisor-only. Technicians hand work INTO review via
	 * the checklist workflow; only managers/admins move it forward (or back)
	 * from there, matching the backend's approver pool (approved_by_id is
	 * "the supervisor/approver who reviewed and approved the finished work"). */
	in_review: [
		{ toStatus: 'approved', label: 'Approve', variant: 'primary', allowedRoles: ['admin', 'manager'] },
		{ toStatus: 'in_progress', label: 'Request Changes', variant: 'secondary', allowedRoles: ['admin', 'manager'] },
		{ toStatus: 'cancelled', label: 'Cancel', variant: 'danger', allowedRoles: ['admin', 'manager'] },
	],
	approved: [
		{ toStatus: 'completed', label: 'Complete', variant: 'primary', allowedRoles: ['admin', 'manager'] },
		{ toStatus: 'cancelled', label: 'Cancel', variant: 'danger', allowedRoles: ['admin', 'manager'] },
	],
	on_hold: [
		{ toStatus: 'in_progress', label: 'Resume Work', variant: 'primary', allowedRoles: ['admin', 'manager', 'technician'] },
		{ toStatus: 'cancelled', label: 'Cancel', variant: 'danger', allowedRoles: ['admin', 'manager'] },
	],
	/* Billing tail — Super Admins only. Raising an invoice and closing the
	 * record behind it are office accounting acts, not field or supervisory
	 * ones: managers and admins own the work up to and including 'completed'.
	 *
	 * The empty allowedRoles is how that is expressed. super_admin and
	 * developer are never listed in any allowedRoles — they pass through the
	 * isPrivileged escape hatch below — so an empty list means "privileged
	 * roles only", not "nobody". The backend enforces the same rule on
	 * PATCH /work-orders/:id, so this is the visible half of a gate that
	 * holds either way. */
	completed: [
		{ toStatus: 'invoiced', label: 'Mark Invoiced', variant: 'primary', allowedRoles: [] },
	],
	invoiced: [
		{ toStatus: 'closed', label: 'Close', variant: 'primary', allowedRoles: [] },
	],
};

/**
 * Inspection status transitions keyed by current status.
 * Note: 'corrections_in_progress' can no longer be entered — the Assign
 * Deficiency step that used to lead there was retired in favour of follow-up
 * work orders — but rows already in it keep their way out below.
 */
const INSPECTION_TRANSITIONS: Record<string, StatusTransition[]> = {
	pending: [
		{ toStatus: 'in_progress', label: 'Start Inspection', variant: 'primary', allowedRoles: ['admin', 'manager', 'technician'] },
		{ toStatus: 'cancelled', label: 'Cancel', variant: 'danger', allowedRoles: ['admin', 'manager'] },
	],
	in_progress: [
		{ toStatus: 'observations_complete', label: 'Complete Observations', variant: 'primary', allowedRoles: ['admin', 'manager', 'technician'] },
		{ toStatus: 'cancelled', label: 'Cancel', variant: 'danger', allowedRoles: ['admin', 'manager'] },
	],
	/* The inspector's sign-off lands here, and the list page files it under
	 * "Pending Review". A clean result has nothing to correct, so the reviewer
	 * closes it out directly — without this step a passing inspection could
	 * only reach 'completed' by inventing a deficiency to assign. Supervisor
	 * tier only: judging that a result needs no follow-up IS the review, and
	 * the inspector who recorded the observations shouldn't review their own.
	 * When an item failed, the remediation path is Create Follow-Up (a work
	 * order spun out of the inspection), not a status step. */
	observations_complete: [
		{ toStatus: 'completed', label: 'Complete Inspection', variant: 'primary', allowedRoles: ['admin', 'manager'] },
	],
	corrections_in_progress: [
		{ toStatus: 'completed', label: 'Complete Corrections', variant: 'primary', allowedRoles: ['admin', 'manager', 'technician'] },
		{ toStatus: 'cancelled', label: 'Cancel', variant: 'danger', allowedRoles: ['admin', 'manager'] },
	],
};

/* ============================================
 * PUBLIC API
 * ============================================ */

/**
 * Returns the status transitions the given user may perform from the given
 * status, in declaration order (forward progress first, cancellation last).
 *
 * @param entityType	- Whether this is a work order or an inspection
 * @param currentStatus - The record's current status string
 * @param userRole		- The acting user's role
 *
 * @example
 * getStatusTransitions('inspection', 'in_progress', 'technician')
 * // [{ toStatus: 'observations_complete', label: 'Complete Observations', … }]
 */
export function getStatusTransitions(entityType: TransitionEntityType, currentStatus: string, userRole: UserRole): StatusTransition[] {
	/* Select the correct transition map based on entity type */
	const transitionMap = entityType === 'work_order' ? WORK_ORDER_TRANSITIONS : INSPECTION_TRANSITIONS;
	const availableTransitions = transitionMap[currentStatus] || [];

	/* Filter transitions to only those the user's role is allowed to perform.
	 * super_admin and developer are platform-level roles that inherit admin
	 * privileges everywhere else in the app (e.g. canEdit on the detail
	 * pages), but they are not listed in any per-transition allowedRoles —
	 * without this they'd see NO status buttons at all. Treat them as
	 * permitted for every transition. */
	const isPrivileged = userRole === 'super_admin' || userRole === 'developer';

	return availableTransitions.filter((t) => isPrivileged || t.allowedRoles.includes(userRole));
}
